// Store the original document title for later updates
const documentTitle = document.title;

// Initialize the facetValueCache when the DOM is ready
document.addEventListener("DOMContentLoaded", () => {
	FacetFiltersForm.initializeFacetValueCache();
	
});

// Custom element for handling facet filters
class FacetFiltersForm extends HTMLElement {
	constructor() {
		super();
		// Set up event handlers for page load and form input
		window.onload = () => {
			FacetFiltersForm.handleCheckboxChange();
			FacetFiltersForm.renderPage(window.location.search, null, true);
		};
		this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

		// Debounce form submission to avoid excessive requests
		this.debouncedOnSubmit = debounce((event) => {
			this.onSubmitHandler(event);
		}, 800);

		const facetForm = this.querySelector("form");
		facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

		const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
		if (facetWrapper) {
			facetWrapper.addEventListener("keyup", onKeyUpEscape);
		}
	}

	// Getter for facet value cache, initializes if not present
	static get facetValueCache() {
		if (!this._facetValueCache) {
			this._facetValueCache = {};
		}
		return this._facetValueCache;
	}

	// Setter for facet value cache
	static set facetValueCache(value) {
		this._facetValueCache = value;
	}

	// Set up popstate event listener for browser navigation
	static setListeners() {
		const onHistoryChange = (event) => {
			const searchParams = event.state
				? event.state.searchParams
				: FacetFiltersForm.searchParamsInitial;
			if (searchParams === FacetFiltersForm.searchParamsPrev) return;
			FacetFiltersForm.renderPage(searchParams, null, false);
		};
		window.addEventListener("popstate", onHistoryChange);
	}

	// Enable or disable active facet remove buttons
	static toggleActiveFacets(disable = true) {
		document.querySelectorAll(".js-facet-remove").forEach((element) => {
			element.classList.toggle("disabled", disable);
		});
	}

	// Main function to render page sections based on search params
	static renderPage(searchParams, event, updateURLHash = true) {
		// Ensure facetValueCache is loaded from sessionStorage if empty
		if (
			!FacetFiltersForm.facetValueCache ||
			Object.keys(FacetFiltersForm.facetValueCache).length === 0
		) {
			const cachedData = sessionStorage.getItem("facetValueCache");
			if (cachedData) {
				try {
					FacetFiltersForm.facetValueCache = JSON.parse(cachedData);
				} catch (error) {
					console.error("Error parsing facetValueCache from sessionStorage", error);
					FacetFiltersForm.facetValueCache = {};
				}
			} else {
				FacetFiltersForm.facetValueCache = {};
			}
		}

		// Clean and process filter params
		searchParams = FacetFiltersForm.processFilterParams(searchParams);

		FacetFiltersForm.searchParamsPrev = searchParams;
		const sections = FacetFiltersForm.getSections();
		const countContainer = document.getElementById("ProductCount");
		const countContainerDesktop = document.getElementById("ProductCountDesktop");
		const loadingSpinners = document.querySelectorAll(
			".facets-container .loading__spinner, facet-filters-form .loading__spinner"
		);

		// Show loading spinners and set loading state
		loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
		document
			.getElementById("ProductGridContainer")
			.querySelector(".collection")
			.classList.add("loading");
		if (countContainer) countContainer.classList.add("loading");
		if (countContainerDesktop) countContainerDesktop.classList.add("loading");

		// Fetch or use cached section HTML for each section
		sections.forEach((section) => {
			const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
			const filterDataUrl = (element) => element.url === url;

			FacetFiltersForm.filterData.some(filterDataUrl)
				? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
				: FacetFiltersForm.renderSectionFromFetch(url, event);
		});

		// Update URL hash if needed and update checkbox states
		if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
		const urlParams = new URLSearchParams(window.location.search);
		const allInputs = document.querySelectorAll(
			"facet-filters-form input[type=checkbox]"
		);

		// Uncheck all inputs by default
		allInputs.forEach((input) => {
			input.checked = false;
			input.closest("label")?.classList.remove("active");
		});

		// Check only those inputs that match URL parameters
		allInputs.forEach((input) => {
			const inputName = input.name;
			const inputValue = input.value;
			if (urlParams.has(inputName)) {
				const paramValues = urlParams.get(inputName).split(",");
				if (paramValues.includes(inputValue)) {
					input.checked = true;
					input.closest("label")?.classList.add("active");
				}
			}
		});
		//FacetFiltersForm.showRemoveAllFilterButton();
	}

	// Clean and process filter params, handling cache and multi-value logic
	static processFilterParams(searchParams) {
		// Remove leading "?" if present
		if (typeof searchParams === "string" && searchParams.startsWith("?")) {
			searchParams = searchParams.slice(1);
		}
		if (
			!FacetFiltersForm.facetValueCache ||
			Object.keys(FacetFiltersForm.facetValueCache).length === 0
		) {
			return searchParams;
		}
		const searchParamsObj = new URLSearchParams(searchParams);
		const result = new URLSearchParams();
		const filterParams = {};

		// Separate filter.p params from others
		for (const [key, value] of searchParamsObj.entries()) {
			if (key.includes("filter.p")) {
				if (!filterParams[key]) filterParams[key] = [];
				filterParams[key].push(value);
			} else {
				result.append(key, value);
			}
		}

		// Handle multi-value and cached values for filters
		for (const [key, values] of Object.entries(filterParams)) {
			const baseValues = new Set(
				values.filter((v) => !v.includes(",") && !v.includes("|"))
			);

			const allValues = new Set(values);

			if (FacetFiltersForm.facetValueCache[key]) {
				FacetFiltersForm.facetValueCache[key].forEach((cached) => {
					const parts = cached.split(/[,|]/).map((p) => p.trim());
					for (const base of baseValues) {
						if (parts.includes(base)) {
							allValues.add(cached);
							break;
						}
					}
				});
			}

			const retainedValues = new Set();

			for (const value of allValues) {
				const parts = value.split(/[,|]/).map((p) => p.trim());
				if (value.includes(",") || value.includes("|")) {
					const keep = Array.from(baseValues).some((base) =>
						parts.includes(base)
					);
					if (keep) {
						result.append(key, value);
						retainedValues.add(value);
					}
				} else {
					result.append(key, value);
					retainedValues.add(value);
				}
			}
		}
		return result.toString();
	}

	// Initialize facet value cache from sessionStorage or populate if missing
	static initializeFacetValueCache() {
		const cachedData = sessionStorage.getItem("facetValueCache");

		if (cachedData) {
			try {
				FacetFiltersForm.facetValueCache = JSON.parse(cachedData);
				FacetFiltersForm.checkForCacheUpdates();
			} catch (error) {
				console.error("Error parsing cachedData", error);
				FacetFiltersForm.populateFacetCache();
			}
		} else {
			FacetFiltersForm.populateFacetCache();
		}
	}

	// Populate facet value cache by fetching all available filter values
	static async populateFacetCache() {
		try {
			const response = await fetch("/search?q=*");
			const html = await response.text();
			const doc = new DOMParser().parseFromString(html, "text/html");
			const containers = doc.querySelectorAll("div[data-key]");

			const newCache = {};

			containers.forEach((container) => {
				const key = container.getAttribute("data-key");
				const checkboxes = container.querySelectorAll(
					'input[type="checkbox"]'
				);
				const values = Array.from(checkboxes).map((cb) => cb.value);
				newCache[key] = values;
			});

			FacetFiltersForm.facetValueCache = newCache;
			sessionStorage.setItem("facetValueCache", JSON.stringify(newCache));

			return newCache;
		} catch (error) {
			console.error("Error populating facet cache", error);
			return null;
		}
	}

	// Check if the cache needs updating and reload if so
	static async checkForCacheUpdates() {
		const currentCache = FacetFiltersForm.facetValueCache;
		const newCache = await FacetFiltersForm.populateFacetCache();

		if (
			newCache &&
			FacetFiltersForm.isCacheDifferent(currentCache, newCache)
		) {
			sessionStorage.setItem("facetValueCache", JSON.stringify(newCache));
			window.location.reload();
		}
	}

	// Compare two caches for differences
	static isCacheDifferent(oldCache, newCache) {
		if (!oldCache) return true;

		const oldKeys = Object.keys(oldCache);
		const newKeys = Object.keys(newCache);

		if (oldKeys.length !== newKeys.length) return true;
		if (oldKeys.some((key) => !newKeys.includes(key))) return true;

		for (const key of oldKeys) {
			const oldValues = oldCache[key] || [];
			const newValues = newCache[key] || [];

			if (oldValues.length !== newValues.length) return true;

			if (
				oldValues.some((val) => !newValues.includes(val)) ||
				newValues.some((val) => !oldValues.includes(val))
			) {
				return true;
			}
		}
		return false;
	}

	// Update checkbox states based on current URL parameters
	static handleCheckboxChange() {
		const urlParams = new URLSearchParams(window.location.search);
		const allInputs = document.querySelectorAll(
			"facet-filters-form input[type=checkbox]:not(input[data-hidden])"
		);

		// Uncheck all inputs first
		allInputs.forEach((input) => {
			input.checked = false;
			input.closest("label")?.classList.remove("active");
		});

		// Build a set of exact match values (ignore any containing ',' or '|')
		const validParamValues = new Map();

		for (const [key, value] of urlParams.entries()) {
			if (!value.includes(",") && !value.includes("|")) {
				if (!validParamValues.has(key)) {
					validParamValues.set(key, new Set());
				}
				validParamValues.get(key).add(value);
			}
		}

		// Check checkboxes only for valid matches
		allInputs.forEach((input) => {
			const inputName = input.name;
			const inputValue = input.value;

			if (
				validParamValues.has(inputName) &&
				validParamValues.get(inputName).has(inputValue)
			) {
				input.checked = true;
				input.disabled = false;
				input.closest("label")?.classList.add("active");
				input.closest("label")?.classList.remove("disabled");
			}
		});

		FacetFiltersForm.reorderFilters();
	}

	// Show or hide the "Remove All" filter button as needed
	static showRemoveAllFilterButton() {
		const wrappers = document.querySelectorAll(
			".active-facets__button-wrapper"
		);

		wrappers.forEach((wrapper) => {
			const container = wrapper.parentElement;
			const facetRemoves = container.querySelectorAll("facet-remove");

			if (
				facetRemoves.length === 1 &&
				wrapper.contains(facetRemoves[0])
			) {
				wrapper.style.display = "none";
			} else {
				wrapper.style.display = "flex";
			}
		});
	}

	// Reorder filters, sorting by scale or moving disabled to the end
	static reorderFilters() {
		const lists = document.querySelectorAll(
			"ul.facets-layout-list[data-filter]"
		);

		lists.forEach((list) => {
			const filterType = list.getAttribute("data-filter");
			const items = Array.from(list.querySelectorAll("li"));
			const sortableItems = [];

			items.forEach((item) => {
				if (item.querySelector("input[data-hidden]")) return;
				sortableItems.push(item);
			});

			// For 'scale' filters, sort numerically largest to smallest by value
			if (filterType === "scale") {
				sortableItems.sort((a, b) => {
					const parseScale = (el) => {
						const text = el.textContent.trim();
						const match = text.match(/1\/([\d.]+)/);
						return match ? 1 / parseFloat(match[1]) : 0;
					};

					const aDisabled = a
						.querySelector("label")
						?.classList.contains("disabled");
					const bDisabled = b
						.querySelector("label")
						?.classList.contains("disabled");

					const aValue = parseScale(a);
					const bValue = parseScale(b);

					// First sort by scale (larger first)
					if (aValue !== bValue) {
						return bValue - aValue;
					}

					// Then push disabled to the end
					if (aDisabled !== bDisabled) {
						return aDisabled ? 1 : -1;
					}

					return 0;
				});
			} else {
				// For non-scale filters: move disabled to the end
				sortableItems.sort((a, b) => {
					const aDisabled = a
						.querySelector("label")
						?.classList.contains("disabled");
					const bDisabled = b
						.querySelector("label")
						?.classList.contains("disabled");

					if (aDisabled && !bDisabled) return 1;
					if (!aDisabled && bDisabled) return -1;
					return 0;
				});
			}

			// Remove and reappend sorted items
			sortableItems.forEach((item) => list.appendChild(item));
		});
	}

	// Fetch section HTML and render it, then update UI
	static renderSectionFromFetch(url, event) {
		fetch(url)
			.then((response) => response.text())
			.then((responseText) => {
				const html = responseText;
				const parser = new DOMParser();
				let parsedHTML = parser.parseFromString(html, "text/html");

				const urlParams = new URLSearchParams(window.location.search);
				FacetFiltersForm.filterData = [
					...FacetFiltersForm.filterData,
					{ html, url },
				];
				FacetFiltersForm.renderFilters(html, event);
				FacetFiltersForm.renderProductGridContainer(html);
				FacetFiltersForm.renderProductCount(html);
				if (typeof initializeScrollAnimationTrigger === "function")
					initializeScrollAnimationTrigger(html.innerHTML);

				const q = urlParams.get("q") || "*";
				const count = FacetFiltersForm.getProductCount(html);
				const countValue = count.split(" ")[0];
				let currentTitle = documentTitle;
				let updatedTitle = currentTitle
					.replace(/:\s*\d+/, `: ${countValue}`)
					.replace(/"[^"]*"/, `"${q}"`);
				document.title = updatedTitle;
				FacetFiltersForm.handleCheckboxChange();
			});
	}

	// Render section from cached HTML
	static renderSectionFromCache(filterDataUrl, event) {
		const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
		FacetFiltersForm.renderFilters(html, event);
		FacetFiltersForm.renderProductGridContainer(html);
		FacetFiltersForm.renderProductCount(html);
		if (typeof initializeScrollAnimationTrigger === "function")
			initializeScrollAnimationTrigger(html.innerHTML);

		const urlParams = new URLSearchParams(window.location.search);
		const q = urlParams.get("q") || "*";
		const count = FacetFiltersForm.getProductCount(html);
		const countValue = count.split(" ")[0];
		let currentTitle = documentTitle;
		let updatedTitle = currentTitle
			.replace(/:\s*\d+/, `: ${countValue}`)
			.replace(/"[^"]*"/, `"${q}"`);
		document.title = updatedTitle;
		FacetFiltersForm.handleCheckboxChange();
	}

	// Update the product grid container with new HTML
	static renderProductGridContainer(html) {
		try {
			const container = document.getElementById("ProductGridContainer");
			if (!container) {
				throw new Error("ProductGridContainer not found");
			}

			const parsedDoc = new DOMParser().parseFromString(
				html,
				"text/html"
			);
			const parsedContainer = parsedDoc.getElementById(
				"ProductGridContainer"
			);

			if (!parsedContainer) {
				throw new Error(
					"Failed to parse ProductGridContainer from HTML"
				);
			}

			container.innerHTML = parsedContainer.innerHTML;

			// Cancel scroll triggers for new content
			container.querySelectorAll(".scroll-trigger").forEach((element) => {
				element.classList.add("scroll-trigger--cancel");
			});
		} catch (error) {
			console.warn("Container not found - reloading...", error);

			let url = window.location.href;
			if (url.includes("/search??"))
				url = url.replace("/search??", "/search?");
			//window.location.href = url;
		}
	}

	// Update the product count display
	static renderProductCount(html) {
		const count = new DOMParser()
			.parseFromString(html, "text/html")
			.getElementById("ProductCount").innerHTML;
		const container = document.getElementById("ProductCount");
		const containerDesktop = document.getElementById("ProductCountDesktop");
		container.innerHTML = count;
		container.classList.remove("loading");
		if (containerDesktop) {
			containerDesktop.innerHTML = count;
			containerDesktop.classList.remove("loading");
		}
		const loadingSpinners = document.querySelectorAll(
			".facets-container .loading__spinner, facet-filters-form .loading__spinner"
		);
		loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
		document.querySelectorAll("input[name=q]").forEach((input) => {
			input.disabled = false;
		});
	}

	// Extract product count from HTML
	static getProductCount(html) {
		const parsedHTML = new DOMParser().parseFromString(html, "text/html");
		const count = parsedHTML
			.getElementById("ProductCount")
			.textContent.trim();
		return count;
	}

	// Render filter UI elements from HTML
	static renderFilters(html, event) {
		const urlParams = new URLSearchParams(window.location.search);
		let parsedHTML = new DOMParser().parseFromString(html, "text/html");

		const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
			"#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
		);
		const facetDetailsElementsFromDom = document.querySelectorAll(
			"#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
		);

		// Remove elements not present in fetched HTML
		Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
			if (
				!Array.from(facetDetailsElementsFromFetch).some(
					({ id }) => currentElement.id === id
				)
			) {
				currentElement.remove();
			}
		});

		const matchesId = (element) => {
			const jsFilter = event
				? event.target.closest(".js-filter")
				: undefined;
			return jsFilter ? element.id === jsFilter.id : false;
		};

		const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
			(element) => !matchesId(element)
		);
		const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
			matchesId
		);

		// Update or insert filter elements
		facetsToRender.forEach((elementToRender, index) => {
			const currentElement = document.getElementById(elementToRender.id);
			if (currentElement) {
				document.getElementById(elementToRender.id).innerHTML =
					elementToRender.innerHTML;
			} else {
				if (index > 0) {
					const {
						className: previousElementClassName,
						id: previousElementId,
					} = facetsToRender[index - 1];
					if (
						elementToRender.className === previousElementClassName
					) {
						document
							.getElementById(previousElementId)
							.after(elementToRender);
						return;
					}
				}

				if (elementToRender.parentElement) {
					document
						.querySelector(
							`#${elementToRender.parentElement.id} .js-filter`
						)
						.before(elementToRender);
				}
			}
		});

		FacetFiltersForm.renderActiveFacets(parsedHTML);
		FacetFiltersForm.renderAdditionalElements(parsedHTML);

		// Render counts for the filter if needed
		if (countsToRender) {
			const closestJSFilterID = event.target.closest(".js-filter").id;

			if (closestJSFilterID) {
				FacetFiltersForm.renderCounts(
					countsToRender,
					event.target.closest(".js-filter")
				);
				FacetFiltersForm.renderMobileCounts(
					countsToRender,
					document.getElementById(closestJSFilterID)
				);

				const newFacetDetailsElement =
					document.getElementById(closestJSFilterID);
				const newElementSelector =
					newFacetDetailsElement.classList.contains(
						"mobile-facets__details"
					)
						? `.mobile-facets__close-button`
						: `.facets__summary`;
				const newElementToActivate =
					newFacetDetailsElement.querySelector(newElementSelector);

				const isTextInput =
					event.target.getAttribute("type") === "text";

				if (newElementToActivate && !isTextInput)
					newElementToActivate.focus();
			}
		}
	}

	// Render active facets (selected filters) in the UI
	static renderActiveFacets(html) {
		const activeFacetElementSelectors = [
			".active-facets-mobile",
			".active-facets-desktop",
		];

		activeFacetElementSelectors.forEach((selector) => {
			const activeFacetsElement = html.querySelector(selector);
			if (!activeFacetsElement) return;
			document.querySelector(selector).innerHTML =
				activeFacetsElement.innerHTML;
		});

		FacetFiltersForm.toggleActiveFacets(false);
	}

	// Render additional UI elements such as mobile open/count/sorting
	static renderAdditionalElements(html) {
		const mobileElementSelectors = [
			".mobile-facets__open",
			".mobile-facets__count",
			".sorting",
		];

		mobileElementSelectors.forEach((selector) => {
			if (!html.querySelector(selector)) return;
			document.querySelector(selector).innerHTML =
				html.querySelector(selector).innerHTML;
		});

		document
			.getElementById("FacetFiltersFormMobile")
			.closest("menu-drawer")
			.bindEvents();
	}

	// Render counts for a filter in the desktop UI
	static renderCounts(source, target) {
		const targetSummary = target.querySelector(".facets__summary");
		const sourceSummary = source.querySelector(".facets__summary");

		if (sourceSummary && targetSummary) {
			targetSummary.outerHTML = sourceSummary.outerHTML;
		}

		const targetHeaderElement = target.querySelector(".facets__header");
		const sourceHeaderElement = source.querySelector(".facets__header");

		if (sourceHeaderElement && targetHeaderElement) {
			targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
		}

		const targetWrapElement = target.querySelector(".facets-wrap");
		const sourceWrapElement = source.querySelector(".facets-wrap");

		if (sourceWrapElement && targetWrapElement) {
			const isShowingMore = Boolean(
				target.querySelector("show-more-button .label-show-more.hidden")
			);
			if (isShowingMore) {
				sourceWrapElement
					.querySelectorAll(".facets__item.hidden")
					.forEach((hiddenItem) =>
						hiddenItem.classList.replace("hidden", "show-more-item")
					);
			}

			targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
		}
	}

	// Render counts for a filter in the mobile UI
	static renderMobileCounts(source, target) {
		const targetFacetsList = target.querySelector(".mobile-facets__list");
		const sourceFacetsList = source.querySelector(".mobile-facets__list");

		if (sourceFacetsList && targetFacetsList) {
			targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
		}
	}

	// Update the browser URL hash with new search params
	static updateURLHash(searchParams) {
		history.pushState(
			{ searchParams },
			"",
			`${window.location.pathname}${
				searchParams && "?".concat(searchParams)
			}`
		);
	}

	// Get the sections to render (usually just the product grid)
	static getSections() {
		return [
			{
				section: document.getElementById("product-grid").dataset.id,
			},
		];
	}

	// Create search params string from a form's data
	createSearchParams(form) {
		const formData = new FormData(form);
		return new URLSearchParams(formData).toString();
	}

	// Handle form submission and trigger page render
	onSubmitForm(searchParams, event) {
		FacetFiltersForm.renderPage(searchParams, event);
	}

	// Handle form input events, build params, and submit
	onSubmitHandler(event) {
		event.preventDefault();
		const sortFilterForms = document.querySelectorAll(
			"facet-filters-form form"
		);
		if (event.srcElement.className == "mobile-facets__checkbox") {
			const searchParams = this.createSearchParams(
				event.target.closest("form")
			);
			this.onSubmitForm(searchParams, event);
		} else {
			const forms = [];
			const isMobile =
				event.target.closest("form").id === "FacetFiltersFormMobile";

			sortFilterForms.forEach((form) => {
				if (!isMobile) {
					if (
						form.id === "FacetSortForm" ||
						form.id === "FacetFiltersForm" ||
						form.id === "FacetSortDrawerForm"
					) {
						forms.push(this.createSearchParams(form));
					}
				} else if (form.id === "FacetFiltersFormMobile") {
					forms.push(this.createSearchParams(form));
				}
			});

			// Join forms and handle empty query parameter more robustly
			let params = forms.join("&");
			params = params.replace(/(\b|&)q=(&|$)/g, "$1q=*$2");

			this.onSubmitForm(params, event);
		}
	}

	// Handle click on an active filter to remove it
	onActiveFilterClick(event) {
		event.preventDefault();
		FacetFiltersForm.toggleActiveFacets();
		const url =
			event.currentTarget.href.indexOf("?") == -1
				? ""
				: event.currentTarget.href.slice(
						event.currentTarget.href.indexOf("?") + 1
				  );
		FacetFiltersForm.renderPage(url);
	}
}

// Static properties and custom element registration
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

// Custom element for price range filter
class PriceRange extends HTMLElement {
	constructor() {
		super();
		// Add event listeners for range inputs
		this.querySelectorAll("input").forEach((element) => {
			element.addEventListener("change", this.onRangeChange.bind(this));
			element.addEventListener("keydown", this.onKeyDown.bind(this));
		});
		this.setMinAndMaxValues();
	}

	// Handle changes to range inputs
	onRangeChange(event) {
		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
	}

	// Restrict key input to valid characters for price
	onKeyDown(event) {
		if (event.metaKey) return;

		const pattern =
			/[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
		if (!event.key.match(pattern)) {
			event.preventDefault();
		}
	}

	// Set min and max attributes for range inputs
	setMinAndMaxValues() {
		const inputs = this.querySelectorAll("input");
		const minInput = inputs[0];
		const maxInput = inputs[1];
		if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
		if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
		if (minInput.value === "") maxInput.setAttribute("data-min", 0);
		if (maxInput.value === "")
			minInput.setAttribute(
				"data-max",
				maxInput.getAttribute("data-max")
			);
	}

	// Adjust input value to be within min/max bounds
	adjustToValidValues(input) {
		const value = Number(input.value);
		const min = Number(input.getAttribute("data-min"));
		const max = Number(input.getAttribute("data-max"));

		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}
}

// Register the price-range custom element
customElements.define("price-range", PriceRange);

// Custom element for removing a facet filter
class FacetRemove extends HTMLElement {
	constructor() {
		super();
		const facetLink = this.querySelector("a");
		facetLink.setAttribute("role", "button");
		facetLink.addEventListener("click", this.closeFilter.bind(this));
		facetLink.addEventListener("keyup", (event) => {
			event.preventDefault();
			if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
		});
	}

	// Handle removing the filter when clicked
	closeFilter(event) {
		event.preventDefault();
		const form =
			this.closest("facet-filters-form") ||
			document.querySelector("facet-filters-form");
		form.onActiveFilterClick(event);
	}
}

// Register the facet-remove custom element
customElements.define("facet-remove", FacetRemove);
